import type { AppointmentOption } from "../../types/index.type";

// One row in the appointment picker. `option` identity is frozen the first
// time we see it — background refreshes may flip the flags below but never
// mutate, move, or replace the option itself.
export interface AppointmentRow {
  option: AppointmentOption;
  /** Appeared in a background refresh after the picker opened — tagged "added". */
  added: boolean;
  /** Missing from the latest refresh — grayed out ("no longer on schedule") and unselectable, never deleted. */
  removed: boolean;
}

export const toAppointmentRows = (options: AppointmentOption[]): AppointmentRow[] =>
  options.map((option) => ({ option, added: false, removed: false }));

/**
 * Append-only merge for background refreshes: a refresh may only append
 * unseen rows at the bottom (tagged "added") and flag missing rows removed.
 * Existing rows never move, re-sort, or change identity — a row shifting
 * under a hovering cursor in a chart picker is a wrong-chart write.
 * Returns `current` (same reference) when nothing changed, so callers can
 * skip the "Updated just now" whisper.
 */
export const mergeAppointmentRows = (current: AppointmentRow[], incoming: AppointmentOption[]): AppointmentRow[] => {
  const incomingIds = new Set(incoming.map((option) => option.id));
  let changed = false;
  const kept = current.map((row) => {
    const removed = !incomingIds.has(row.option.id);
    if (removed === row.removed) return row;
    changed = true;
    return { ...row, removed };
  });

  const knownIds = new Set(current.map((row) => row.option.id));
  const appended = incoming
    .filter((option) => !knownIds.has(option.id))
    .map((option): AppointmentRow => ({ option, added: true, removed: false }));

  if (!changed && appended.length === 0) return current;
  return [...kept, ...appended];
};
